import { cn } from '@/lib/utils';

/** Tone of the medallion — drives the soft glow behind the tinted circle. */
export type MedallionTone = 'positive' | 'warning' | 'neutral';

/** Behind-glow tint per tone (semantic tokens → themed; the circle SVG carries the flat tint). */
const GLOW_CLASSES: Record<MedallionTone, string> = {
  positive: 'bg-success/20',
  warning: 'bg-warning/20',
  neutral: 'bg-accent/10',
};

export interface MedallionProps {
  /** The tinted circle background — exported from Figma as an SVG (vector). */
  circleSrc: string;
  /** The 3D illustration sitting inside the circle — exported as a PNG (raster). */
  illustrationSrc: string;
  /** Alt text for the illustration (the circle is decorative). */
  alt?: string;
  /** Tone → the soft glow behind the circle (the circle SVG already carries the flat tint). */
  tone?: MedallionTone;
  /** Container edge, px. Figma medallion container is 190. */
  size?: number;
  /** Illustration edge, px. Defaults to ~60% of the container. */
  illustrationSize?: number;
  className?: string;
}

/**
 * The pop-up's hero medallion: a tinted circle (SVG) with a 3D illustration
 * (PNG) centered on it. The circle SVG encodes the tone tint per the Figma
 * assets (green / peach / purple); the `tone` prop adds a matching soft glow so
 * the shape still reads on a themed dark surface where the flat light tint would
 * otherwise float.
 */
export function Medallion({
  circleSrc,
  illustrationSrc,
  alt = '',
  tone = 'neutral',
  size = 190,
  illustrationSize,
  className,
}: MedallionProps) {
  const illoSize = illustrationSize ?? Math.round(size * 0.6);
  return (
    <div
      className={cn('relative flex shrink-0 items-center justify-center', className)}
      style={{ width: size, height: size }}
      data-testid="popup-medallion"
    >
      <span aria-hidden className={cn('absolute inset-2 rounded-full blur-2xl', GLOW_CLASSES[tone])} />
      <img src={circleSrc} alt="" aria-hidden className="absolute inset-0 size-full" />
      <img
        src={illustrationSrc}
        alt={alt}
        className="relative"
        style={{ width: illoSize, height: illoSize, objectFit: 'contain' }}
      />
    </div>
  );
}
